interface Global {
  document: Document
  bethnet?: (config: BethnetConfig) => Promise<BethnetAPI>
  riot?: {
    mount: (element: string, api: BethnetAPI) => void
  }
}

const insertStylesheet = async (document: Document, href: string): Promise<void> => {
  const style = document.createElement('link')
  style.setAttribute('rel', 'stylesheet')
  style.setAttribute('type', 'text/css')
  style.setAttribute('href', href)
  if (document.head) {
    document.head.appendChild(style)
  }
}

const insertScript = (document: Document, src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.setAttribute('src', src)
    script.onload = (): void => resolve()
    script.onerror = (): void => reject(new Error(`Unable to load script: ${src}`))
    if (document.body) {
      document.body.appendChild(script)
    }
  })

export type BethnetConfig = Record<string, unknown>

export interface BethnetAPI {
  showLogin?: () => void
  logout?: () => void
  store: {
    subscribe: (listener: () => void) => () => void
    getState: () => {
      cogs?: {
        idToken?: string
        authenticating?: boolean
        loginIntent?: boolean
      }
      auth?: Record<string, unknown> | boolean
    }
  }
}

export type InitBethnet = (config: BethnetConfig) => Promise<BethnetAPI | null>

// loadBNetCoreComponents function to initialize the bethesda.net header with the provided configuration, then appending to the DOM
const loadBNetCoreComponents = async (
  globalWindow: Global | null = typeof window !== 'undefined'
    ? (window as unknown as Global)
    : null,
  baseUri: string,
  {
    pathToStylesheet = '/shared/core/3/global.css',
    pathToScript = '/shared/core/3/global.js',
  }: {
    pathToStylesheet?: string
    pathToScript?: string
  } = {},
): Promise<InitBethnet> => {
  const { document } = globalWindow || {}
  if (!document) {
    return (): Promise<null> => Promise.resolve(null)
  }

  await insertStylesheet(document, `${baseUri}${pathToStylesheet}`)

  if (document.body) {
    document.body.prepend(document.createElement('app'))
    document.body.appendChild(document.createElement('globalfooter'))

    // Inject style to hide #global-login-signup inside <app>
    const style = document.createElement('style')
    style.textContent = `
        /* Hides the global login/signup button in the header */
        app #global-login-signup {
            display: none !important;
        }
        /* Hides whitespace added for some reason */
        .RiotWrapper-cogs-global-nav-RiotWrapper-cogs-global-nav24 {
            height: 0 !important;
        }`

    document.head.appendChild(style)
  }

  await insertScript(document, `${baseUri}${pathToScript}`)

  return async (bnConfig: BethnetConfig): Promise<BethnetAPI | null> => {
    const { bethnet, riot } = globalWindow || {}
    if (typeof bethnet !== 'function' || typeof riot?.mount !== 'function') {
      return null
    }

    const api = await bethnet(bnConfig)
    riot.mount('app', api)
    riot.mount('globalfooter', api)
    return api
  }
}

export default loadBNetCoreComponents
